import {
  Component,
  HostBinding,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { initializeApp } from 'firebase/app';
import { getMessaging, onMessage } from 'firebase/messaging';
import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs';
import { firebaseConfig } from 'server/helpers/push-config';
import { getNewUnixTimestamp } from 'server/shared/helpers/removeTimeZoneOffset';
import { LocaleEnum } from 'server/shared/models/locale.enum';
import { User } from 'server/shared/models/user';
import { hasCypressRunning } from 'src/helpers/hasCypressRunning';
import { compareBuildInfo } from '../helpers/compareBuildInfo';
import { getPushToken } from '../helpers/getPushToken';
import {
  iOSPushCapability,
  iOSPushPostMessage,
} from '../helpers/iOSPushCapability';
import { ToastService } from './services/toast/toast.service';
import { TranslationService } from './services/translation.service';
import { UserService } from './services/user.service';
import { ConfirmationDialog } from './shared/confirmation-dialog/confirmation.component';
import { SidenavService } from './shared/sidenav.service';
import { SubscriptionService } from './shared/subscription.service';
import { DemoUserSetupModalComponent } from './users/demo-user-setup-modal/demo-user-setup-modal.component';

let isEmpty = require('lodash/isEmpty');

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild('sidenav', { static: false })
  public sidenav?: MatSidenav;
  private bemPrefix: string = 'app-component';
  private user?: User;
  locales = Object.keys(LocaleEnum);
  userSignedIn$ = this.userService.isLoggedIn$;
  i18n = this.translationService.getTranslations();

  iOSPushCapability = false;

  constructor(
    private sidenavService: SidenavService,
    private router: Router,
    private ngZone: NgZone,
    private subscriptionService: SubscriptionService,
    private toastService: ToastService,
    private translationService: TranslationService,
    private userService: UserService,
    private dialog: MatDialog,
  ) {
    Session.set('locale', 'de');
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.isWrapperlessPage =
          window.location.pathname === '/welcome' ||
          window.location.pathname.startsWith('/enroll-account') ||
          window.location.pathname.startsWith('/login') ||
          window.location.pathname.startsWith('/forgot-password') ||
          window.location.pathname.startsWith('/2fa') ||
          window.location.pathname.startsWith('/reset-password') ||
          (window.location.pathname.startsWith('/jobs/') &&
            window.location.pathname.includes('/invitees/')) ||
          (window.location.pathname.startsWith('/jobs/') &&
            window.location.pathname.includes('/time-tracking/')) ||
          (window.location.pathname.startsWith('/temp-work/employments/') &&
            window.location.pathname.includes('/invitees/'));
      }
    });

    const app = initializeApp(firebaseConfig);
    const messaging = getMessaging(app);

    onMessage(messaging, (payload) => {
      if (!payload.notification?.title) return;
      this.toastService.add({
        type: 'INFO',
        message: payload.notification.title,
        timeout: 3000,
      });
    });

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('../sw.js').then();
    }
  }

  user$ = this.userService.readyUser$;

  userInDemoSetup$ = this.user$.pipe(
    take(1),
    filter((user) => !!user.isInDemoSetup),
  );

  userFinishedDemoSetup$ = this.userInDemoSetup$.pipe(
    switchMap((user) => {
      return this.userService.readyUser$.pipe(
        filter((user) => !user.isInDemoSetup),
        take(1),
      );
    }),
  );

  isInEnrollPage$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event) => {
      return (event as NavigationEnd).urlAfterRedirects.startsWith(
        '/enroll-account',
      );
    }),
  );

  demoUserSetupDialogRef?: MatDialogRef<DemoUserSetupModalComponent>;
  openDemoModalEffect$ = combineLatest([
    this.userInDemoSetup$,
    this.isInEnrollPage$,
  ]).pipe(
    tap(([user, isInEnrollPage]) => {
      // we dont need the blocker modal when the user tries to set his Password
      if (isInEnrollPage) {
        return;
      }
      this.demoUserSetupDialogRef = this.dialog.open(
        DemoUserSetupModalComponent,
        {
          data: {
            user,
          },
          disableClose: true,
        },
      );
    }),
  );

  finishDemoUserSetupEffect$ = this.userFinishedDemoSetup$.pipe(
    tap(() => {
      this.demoUserSetupDialogRef?.close();
    }),
  );

  @HostBinding('class.app-component--url-login') isLoggedOut: boolean = true;
  @HostBinding('class.app-component--url-welcome') isWrapperlessPage: boolean =
    true;

  ngOnInit() {
    this.openDemoModalEffect$.subscribe();
    this.finishDemoUserSetupEffect$.subscribe();
    // subscribe criteria without user
    this.subscriptionService.certificates.subscribe();
    this.subscriptionService.specialities.subscribe();
    Tracker.autorun(() => {
      if (Meteor.userId()) {
        this.subscriptionService.actionRequiredUsersCount.subscribe();
        this.subscriptionService.actionRequiredJobsCount.subscribe();
        this.subscriptionService.actionRequiredEmploymentsCount.subscribe();
        this.subscriptionService.jobRequestsCount.subscribe();
        this.subscriptionService.subscribe();
      }
    });
    this.userService.readyUser$.subscribe((res) => {
      this.user = res;
      Session.set('role', res.role);

      // @ts-ignore
      if (this.user.locale && !hasCypressRunning()) {
        Session.set('locale', this.user.locale);
      }
      if (
        this.user.isInSetup &&
        !this.user.phoneConfirmed &&
        !window.location.pathname.startsWith('/enroll-account') &&
        !window.location.pathname.startsWith('/welcome')
      ) {
        this.router.navigate(['/welcome']).then();
      }

      this.handlePush().then();

      if (iOSPushCapability()) {
        this.iOSPushCapability = true;
        try {
          // @ts-ignore
          window.navigator.clearAppBadge();
        } catch (err) {
          console.log('Can not clear app badge', err);
        }
      }

      const isIos = () => {
        const userAgent = window.navigator.userAgent.toLowerCase();
        return /iphone|ipad|ipod/.test(userAgent);
      };
      const isInStandaloneMode = () =>
        // @ts-ignore
        'standalone' in window.navigator && window.navigator.standalone;

      if (isIos() && !isInStandaloneMode() && this.iOSPushCapability) {
        iOSPushPostMessage();
      }

      // @ts-ignore
      window.addEventListener('push-token', (event: CustomEvent) => {
        if (event && event.detail) {
          Meteor.call('updatePushToken', event.detail);
        }
      });
    });

    Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.isLoggedOut = !Meteor.userId();
        if (this.isLoggedOut) {
          const browserLanguage = navigator.language
            ?.split('-')?.[0]
            ?.toLowerCase();
          if (
            browserLanguage &&
            this.locales?.includes(browserLanguage.toUpperCase()) &&
            //  @ts-ignore
            !hasCypressRunning()
          ) {
            Session.set('locale', browserLanguage);
          }
        }
      });
    });

    Tracker.autorun(() => {
      let status = Meteor.status();
      if (status && status.connected) {
        compareBuildInfo().then((mismatch) => {
          if (!mismatch) return;
          const dialogRef = this.dialog.open(ConfirmationDialog, {
            data: {
              title: this.i18n.newVersionTitle,
              text: this.i18n.newVersionText,
              singleAction: this.i18n.reload,
            },
          });
          dialogRef.afterClosed().subscribe((result: string) => {
            window.location.reload();
          });
        });
      }
      if (status && !status.connected && status.status == 'waiting') {
        if (!status.retryTime) {
          return;
        }
        let timeout =
          Math.floor(status.retryTime) - getNewUnixTimestamp() * 1000;
        if (timeout > 1000) {
          this.toastService.add({
            type: 'ERROR',
            message: 'connectionLost',
            timeout: timeout,
          });
        }
      }
      if (status && !status.connected && status.status == 'offline') {
        this.toastService.add({
          type: 'ERROR',
          message: 'connectionOffline',
          timeout: 10000000,
        });
      }
    });
  }

  public get componentClasses(): string {
    let tmp: Array<string> = [this.bemPrefix];

    if (this.sidenavService.isOpened) {
      tmp.push(`${this.bemPrefix}--state-sidenav-opened`);
    } else if (this.sidenavService.isClosed) {
      tmp.push(`${this.bemPrefix}--state-sidenav-closed`);
    }

    return tmp.join(' ');
  }

  public onBackgroundClick(): void {
    this.sidenavService.toggle();
  }

  async handlePush() {
    if (!this.user) return;
    try {
      let token = await getPushToken(this.user);
      if (
        token &&
        !this.user.pushTokens
          ?.map((pushToken) => pushToken.token)
          .includes(token)
      ) {
        Meteor.call('updatePushToken', token);
      }
    } catch (e: any) {
      if (e?.message.includes('(messaging/permission-blocked)')) {
        console.log('[Push] Push notifications disabled by the browser');
      } else {
        console.error(e);
      }
    }
  }
}
